import { SimpleGrid, Stack, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { IMetaPage, MetaIntegration } from '@feathr/blackbox';
import { Heading } from '@feathr/components';

import MetaConnectionDetails from '../MetaConnectionDetails';
import MetaFacebookPagesCard from '../MetaFacebookPagesCard';
import MetaInstagramAccountsCard from '../MetaInstagramAccountsCard';

interface IMetaConnectionOverviewProps {
  integration: MetaIntegration | null;
  pages: IMetaPage[];
}

function MetaConnectionOverview({
  integration,
  pages,
}: Readonly<IMetaConnectionOverviewProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const hasConnectionDetails = !!integration;
  const hasFacebookPages = pages.length > 0;
  const instagramPages = pages.filter((p) => p.instagram_account_id);
  const hasInstagramAccounts = instagramPages.length > 0;

  // Determine the number of columns for the grid based on available data
  const numberOfColumns = [hasConnectionDetails, hasFacebookPages, hasInstagramAccounts].filter(
    Boolean,
  ).length;

  return (
    <Stack gap={theme.spacing[4]}>
      <Heading order={3} variant={'h3'}>
        {t('Connection Overview')}
      </Heading>
      <SimpleGrid cols={numberOfColumns}>
        {hasFacebookPages && <MetaFacebookPagesCard pages={pages} />}
        {hasInstagramAccounts && <MetaInstagramAccountsCard pages={instagramPages} />}
        <MetaConnectionDetails
          connectedAt={integration?.connectedAt}
          connectedByEmail={integration?.connectedBy?.email}
          connectedByName={integration?.connectedBy?.name}
        />
      </SimpleGrid>
    </Stack>
  );
}

export default MetaConnectionOverview;
